<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <page-header
      :header-title="$t('INBOX_MGMT.ADD.RCS.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.RCS.DESC')"
    />
    <form class="mx-0 flex flex-wrap" @submit.prevent="createChannel()">
      <!-- Channel Name -->
      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: $v.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.RCS.CHANNEL_NAME.LABEL') }}
          <input
            v-model.trim="channelName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.RCS.CHANNEL_NAME.PLACEHOLDER')"
            @blur="$v.channelName.$touch"
          />
          <span v-if="$v.channelName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.RCS.CHANNEL_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Provider selector -->
      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label>
          {{ $t('INBOX_MGMT.ADD.RCS.PROVIDER.LABEL') }}
          <select v-model="provider">
            <option value="twilio">Twilio</option>
            <option value="sinch">Sinch</option>
          </select>
        </label>
      </div>

      <!-- Twilio fields -->
      <template v-if="provider === 'twilio'">
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label :class="{ error: $v.accountSID.$error }">
            {{ $t('INBOX_MGMT.ADD.RCS.ACCOUNT_SID.LABEL') }}
            <input
              v-model.trim="accountSID"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.RCS.ACCOUNT_SID.PLACEHOLDER')"
              @blur="$v.accountSID.$touch"
            />
            <span v-if="$v.accountSID.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.RCS.ACCOUNT_SID.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label :class="{ error: $v.authToken.$error }">
            {{ $t('INBOX_MGMT.ADD.RCS.AUTH_TOKEN.LABEL') }}
            <input
              v-model.trim="authToken"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.RCS.AUTH_TOKEN.PLACEHOLDER')"
              @blur="$v.authToken.$touch"
            />
            <span v-if="$v.authToken.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.RCS.AUTH_TOKEN.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label :class="{ error: $v.agentAddress.$error }">
            {{ $t('INBOX_MGMT.ADD.RCS.AGENT_ADDRESS.LABEL') }}
            <input
              v-model.trim="agentAddress"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.RCS.AGENT_ADDRESS.PLACEHOLDER')"
              @blur="$v.agentAddress.$touch"
            />
            <span v-if="$v.agentAddress.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.RCS.AGENT_ADDRESS.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label>
            {{ $t('INBOX_MGMT.ADD.RCS.PHONE_NUMBER_SMS.LABEL') }}
            <input
              v-model.trim="phoneNumberSms"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.RCS.PHONE_NUMBER_SMS.PLACEHOLDER')"
            />
          </label>
        </div>
      </template>

      <!-- Sinch fields -->
      <template v-if="provider === 'sinch'">
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label :class="{ error: $v.projectID.$error }">
            {{ $t('INBOX_MGMT.ADD.RCS.PROJECT_ID.LABEL') }}
            <input
              v-model.trim="projectID"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.RCS.PROJECT_ID.PLACEHOLDER')"
              @blur="$v.projectID.$touch"
            />
            <span v-if="$v.projectID.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.RCS.PROJECT_ID.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label :class="{ error: $v.appID.$error }">
            {{ $t('INBOX_MGMT.ADD.RCS.APP_ID.LABEL') }}
            <input
              v-model.trim="appID"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.RCS.APP_ID.PLACEHOLDER')"
              @blur="$v.appID.$touch"
            />
            <span v-if="$v.appID.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.RCS.APP_ID.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label :class="{ error: $v.keyID.$error }">
            {{ $t('INBOX_MGMT.ADD.RCS.KEY_ID.LABEL') }}
            <input
              v-model.trim="keyID"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.RCS.KEY_ID.PLACEHOLDER')"
              @blur="$v.keyID.$touch"
            />
            <span v-if="$v.keyID.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.RCS.KEY_ID.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label :class="{ error: $v.keySecret.$error }">
            {{ $t('INBOX_MGMT.ADD.RCS.KEY_SECRET.LABEL') }}
            <input
              v-model.trim="keySecret"
              type="password"
              :placeholder="$t('INBOX_MGMT.ADD.RCS.KEY_SECRET.PLACEHOLDER')"
              @blur="$v.keySecret.$touch"
            />
            <span v-if="$v.keySecret.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.RCS.KEY_SECRET.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
          <label>
            {{ $t('INBOX_MGMT.ADD.RCS.API_REGION.LABEL') }}
            <select v-model="apiRegion">
              <option value="us">US (United States)</option>
              <option value="eu">EU (Europe)</option>
              <option value="br">BR (Brazil)</option>
            </select>
          </label>
        </div>
      </template>

      <div class="w-full">
        <woot-submit-button
          :loading="uiFlags.isCreating"
          :button-text="$t('INBOX_MGMT.ADD.RCS.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import PageHeader from '../../SettingsSubPageHeader.vue';

export default {
  components: { PageHeader },
  mixins: [alertMixin],
  data() {
    return {
      provider: 'twilio',
      channelName: '',
      // Twilio
      accountSID: '',
      authToken: '',
      agentAddress: '',
      phoneNumberSms: '',
      // Sinch
      projectID: '',
      appID: '',
      keyID: '',
      keySecret: '',
      apiRegion: 'us',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    isTwilio() {
      return this.provider === 'twilio';
    },
  },
  validations() {
    return {
      channelName: { required },
      accountSID: { required: requiredIf(() => this.isTwilio) },
      authToken: { required: requiredIf(() => this.isTwilio) },
      agentAddress: { required: requiredIf(() => this.isTwilio) },
      projectID: { required: requiredIf(() => !this.isTwilio) },
      appID: { required: requiredIf(() => !this.isTwilio) },
      keyID: { required: requiredIf(() => !this.isTwilio) },
      keySecret: { required: requiredIf(() => !this.isTwilio) },
    };
  },
  methods: {
    async createChannel() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      const payload = { name: this.channelName, provider: this.provider };

      if (this.isTwilio) {
        Object.assign(payload, {
          account_sid: this.accountSID,
          auth_token: this.authToken,
          agent_address: this.agentAddress,
          phone_number_sms: this.phoneNumberSms,
        });
      } else {
        Object.assign(payload, {
          project_id: this.projectID,
          app_id: this.appID,
          key_id: this.keyID,
          key_secret: this.keySecret,
          api_region: this.apiRegion,
        });
      }

      try {
        await this.$store.dispatch('inboxes/createRcsChannel', {
          rcs_channel: payload,
        });
        this.showAlert(this.$t('INBOX_MGMT.ADD.RCS.API.SUCCESS_MESSAGE'));
        this.$router.push({ name: 'settings_inboxes_page' });
      } catch {
        this.showAlert(this.$t('INBOX_MGMT.ADD.RCS.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>
